<script setup lang="ts">
/**
 * Компонент тоста
 * Уведомление с авто-закрытием
 */

import { onMounted } from 'vue'
import type { Toast } from '@/types'

interface Props {
  toast: Toast
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: [id: string]
}>()

// Иконки для разных типов
const icons = {
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
}

// Цвета для разных типов (семантические статусы, не брендовый акцент).
// Тёмная тема: плотная тёмная плашка со светлым текстом, а не светлая плашка,
// иначе тост будет выбиваться ярким пятном на тёмном фоне.
const colors = {
  success: 'bg-green-900/95 text-green-200 border-green-700',
  error: 'bg-red-900/95 text-red-200 border-red-700',
  warning: 'bg-yellow-900/95 text-yellow-200 border-yellow-700',
  info: 'bg-blue-900/95 text-blue-200 border-blue-700',
}

// Закрытие
function close(): void {
  emit('close', props.toast.id)
}

// Автозакрытие
onMounted(() => {
  if (props.toast.duration && props.toast.duration > 0) {
    setTimeout(close, props.toast.duration)
  }
})
</script>

<template>
  <div
    class="flex items-center gap-3 px-4 py-3 border shadow-lg max-w-md"
    :class="colors[toast.type]"
    role="alert"
  >
    <!-- Иконка -->
    <svg
      class="w-5 h-5 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        :d="icons[toast.type]"
      />
    </svg>
    
    <!-- Сообщение -->
    <p class="text-sm font-medium flex-1">
      {{ toast.message }}
    </p>
    
    <!-- Кнопка закрытия -->
    <button
      type="button"
      class="flex-shrink-0 hover:opacity-70 transition-opacity"
      @click="close"
    >
      <svg
        class="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  </div>
</template>
